import React, { useRef, useState } from 'react';
import {
  composeValidators,
  isEmail,
  isPhone,
  isRequired,
  phoneNumberMask,
  phoneNumberUnmask,
  COOKIES_CONFIG,
} from '@belong/common';
import {
  AppleLogin,
  Button,
  Divider,
  FacebookLogin,
  Form,
  GoogleLogin,
  Image,
  Link,
  Modal,
  Spinner,
  Text,
  TextField,
} from '@belong/ui';
import type {
  AppleResponse,
  GoogleAuthResponse,
  GoogleAuthErrorResponse,
  GoogleAuthUnrelatedErrorResponse,
  GoogleLoginError,
  ReactFacebookFailureResponse,
  ReactFacebookLoginInfo,
} from '@belong/ui';
import clsx from 'clsx';
import { FormikProps } from 'formik';
import { setCookie } from 'nookies';
import { ExternalLoginModel, UserModel } from 'src/api/models';
import { AgreeToTermsFooter } from 'src/features/auth/agree-to-terms-footer/agree-to-terms-footer';
import {
  DEFAULT_REGION_PHONE_NUMBER,
  EMAIL_TAKEN_API_CODE,
} from 'src/features/auth/constants';
import { AUTH_STRINGS } from 'src/features/auth/strings';
import { EnterContactModal } from 'src/features/auth/enter-contact-modal/enter-contact-modal';
import { LoginProvider, LoginProviders } from 'src/features/auth/queries';
import { useExternalLogin, useRegister } from 'src/features/auth/queries';
import { useMutateUser } from 'src/features/users/useUser';
import { useModal } from 'src/hooks/useModal';
import { useStore } from 'src/store';
import { pushModalSelector } from 'src/store/modal';
import { regionSelector } from 'src/store/region';
import { setJwtToken } from 'src/utils/api-client';
import { ApiError } from 'src/utils/build-api-client';
import { setEnhancedConvertionData } from 'src/utils/tracking';
import { object, string, optional, is } from 'superstruct';
import { useTrackPersonalDetailsMutation } from '../../queries';
import css from './qualify-modal.module.css';
import { QUALIFY_MODAL_STRINGS } from './strings';
import { VerticalSlideAnimation } from './vertical-slide-animation/vertical-slide-animation';

const SUBTITLE_ANIMATION_ELEMENTS = [
  <div className="flex items-center" key={0}>
    <div className={clsx(css.fiveStarsImg, 'mx-2xs')}>
      <Image src="/icons/5_stars.svg" alt="" />
    </div>
    on Google
  </div>,
  '66% Less Vacancy',
  'Guaranteed Rent Payments',
  'Exceptional Care',
  'Complimentary Walkthrough',
  'Complimentary Pricing Plan',
];

const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '';
const googleOauthClientId =
  process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID || '';
const redirectURI = process.env.NEXT_PUBLIC_HOST || '';
const TOKEN_EMAIL_MISMATCH = 'TOKEN_EMAIL_MISMATCH';
const ReactFacebookFailureResponseStruct = object({
  status: optional(string()),
});

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

type QualifyModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  isLoading: boolean;
  onSuccessfulLogin: (user?: UserModel) => void;
  shouldClearModals?: boolean;
};

export function QualifyModal({
  isOpen,
  onDismiss,
  isLoading: isLoadingProp,
  onSuccessfulLogin,
  shouldClearModals,
}: QualifyModalProps): React.ReactElement {
  const { isRegisterLoading, register } = useRegister();
  const [
    isEnterContactModalOpen,
    openEnterContactModal,
    closeEnterContactModal,
  ] = useModal();
  const [errorNode, setErrorNode] = useState<string | React.ReactNode>('');
  const { isLoading: isUserLoading, mutateAsync: fetchUser } = useMutateUser();
  const { isExternalLoginLoading, externalLogin } = useExternalLogin();
  const isLoading =
    isLoadingProp ||
    isUserLoading ||
    isExternalLoginLoading ||
    isRegisterLoading;
  const [loginProvider, setLoginProvider] =
    useState<LoginProviders>('Facebook');
  const formRef = useRef<FormikProps<FormValues>>(null);
  const { trackPersonalDetails } = useTrackPersonalDetailsMutation();
  const { information: regionInformation } = useStore(regionSelector);

  const phoneNumber =
    regionInformation?.contactInformation?.phone ?? DEFAULT_REGION_PHONE_NUMBER;

  const pushModal = useStore(pushModalSelector);

  async function handleSubmit(values: FormValues) {
    setErrorNode('');
    try {
      // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
      const registerResponse = await register(values);
      if (!registerResponse.token) throw new Error();

      setEnhancedConvertionData(values);
      setJwtToken(registerResponse.token);
      setCookie(
        null,
        COOKIES_CONFIG.JWT_TOKEN.name,
        registerResponse.token,
        COOKIES_CONFIG.JWT_TOKEN.options,
      );
      setCookie(
        null,
        COOKIES_CONFIG.REFRESH_TOKEN.name,
        registerResponse.refreshToken!,
        COOKIES_CONFIG.REFRESH_TOKEN.options,
      );
      await onSuccessfulLogin();
    } catch (error) {
      setErrorNode(
        error instanceof ApiError && error.code === EMAIL_TAKEN_API_CODE
          ? AUTH_STRINGS['error.email_taken']
          : AUTH_STRINGS['error.generic'],
      );
    }
  }

  function handleSignIn() {
    pushModal({ type: 'LOGIN', props: { onSuccessfulLogin } });
  }

  async function finishExternalLogin(
    loginRequest: ExternalLoginModel,
    provider: LoginProviders,
  ) {
    await externalLogin(loginRequest);
    const user = await fetchUser();

    if (!user.phone || !user.email) {
      setLoginProvider(provider);
      openEnterContactModal();
      return;
    }

    if (onSuccessfulLogin) {
      await onSuccessfulLogin();
    }
  }

  async function handleFacebookSubmit(
    facebookResponse: ReactFacebookFailureResponse | ReactFacebookLoginInfo,
  ) {
    if (is(facebookResponse, ReactFacebookFailureResponseStruct)) {
      return;
    }

    const facebookLoginRequest = {
      loginProvider: LoginProvider.Facebook,
      accessToken: facebookResponse.accessToken,
      // email: registerScreenProps.emailAddress,
    };

    try {
      await finishExternalLogin(facebookLoginRequest, LoginProvider.Facebook);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === TOKEN_EMAIL_MISMATCH) {
          setErrorNode(
            <>
              <Text
                variant="p1"
                fontWeight="semibold"
                className="py-xs text-red text-center"
              >
                {QUALIFY_MODAL_STRINGS['login.fb_login_email_mismatch_line_1']}
              </Text>
              <Text
                variant="p1"
                fontWeight="semibold"
                className="py-xs text-red text-center"
              >
                {QUALIFY_MODAL_STRINGS['login.fb_login_email_mismatch_line_2']}
              </Text>
            </>,
          );
        }
      }

      setErrorNode(QUALIFY_MODAL_STRINGS['generic.something_went_wrong']);
    }
  }

  async function handleGoogleSuccess(response: GoogleAuthResponse) {
    try {
      const { code } = response as GoogleAuthResponse;

      const googleLoginRequest: ExternalLoginModel = {
        loginProvider: 'Google',
        accessToken: code,
        loginExperiment: 'GoogleAuthFlow',
      };

      await finishExternalLogin(googleLoginRequest, 'Google');
    } catch (error) {
      setErrorNode(QUALIFY_MODAL_STRINGS['generic.something_went_wrong']);
    }
  }

  function handleGoogleFailure(
    error:
      | GoogleLoginError
      | GoogleAuthErrorResponse
      | GoogleAuthUnrelatedErrorResponse,
  ) {
    /**
     * When the user closes the popup, we don't want to show an error message.
     */
    if ((error as GoogleAuthUnrelatedErrorResponse).type === 'popup_closed') {
      return;
    }

    if (error === 'INIT_FAILED') {
      setErrorNode(QUALIFY_MODAL_STRINGS['login.google_third_party_error']);

      return;
    }

    setErrorNode(QUALIFY_MODAL_STRINGS['generic.something_went_wrong']);
  }

  async function handleAppleCallback(res: AppleResponse) {
    if (res.authorization) {
      const appleLoginRequest: ExternalLoginModel = {
        loginProvider: 'Apple',
        accessToken: res.authorization.id_token,
        ...(res.user && {
          email: res.user.email,
          firstName: res.user.name.firstName,
          lastName: res.user.name.lastName,
        }),
      };
      try {
        await finishExternalLogin(appleLoginRequest, 'Apple');
      } catch (e) {
        console.error(e);

        setErrorNode(QUALIFY_MODAL_STRINGS['generic.something_went_wrong']);
      }
    } else {
      console.error(res);

      // res.error may have additional information
      setErrorNode(QUALIFY_MODAL_STRINGS['generic.something_went_wrong']);
    }
  }

  function trackUserDetails() {
    const formValues = formRef?.current?.values;
    if (!formValues) return;

    const validEntries = Object.entries(formValues).filter(
      ([, element]) => element !== '',
    );
    if (!validEntries.length) return;

    const validValues = Object.fromEntries(validEntries);
    trackPersonalDetails(validValues);
  }

  function handleDismiss() {
    trackUserDetails(); // in case the user presses the escape key
    onDismiss();
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onDismiss={handleDismiss}
        name="qualify_modal"
        variant="light"
      >
        {isLoading && <Spinner />}
        <div
          className={clsx(
            'py-2xl md:pt-xl container relative flex flex-col items-center md:pb-0',
          )}
        >
          <div className="mb-lg">
            <Text variant="h2" fontWeight="semibold" className="text-center">
              {QUALIFY_MODAL_STRINGS['modal.title']}
            </Text>
          </div>
          <div className="mb-lg">
            <VerticalSlideAnimation elements={SUBTITLE_ANIMATION_ELEMENTS} />
          </div>

          {/* TODO: ask where to put this */}
          {typeof errorNode !== 'string' ? (
            errorNode
          ) : (
            <Text
              variant="p1"
              fontWeight="semibold"
              className="text-red text-center"
            >
              {errorNode}
            </Text>
          )}

          <div
            className={clsx('flex w-full flex-col items-center', css.content)}
          >
            <Form
              initialValues={initialValues}
              onSubmit={handleSubmit}
              innerRef={formRef}
            >
              {() => {
                return (
                  <div className="w-full">
                    <div className="mb-lg gap-x-lg gap-y-sm md:mb-xl grid grid-cols-2">
                      <div className="col-span-full md:col-span-1">
                        <TextField
                          name="firstName"
                          label={QUALIFY_MODAL_STRINGS['form.firstName']}
                          validate={isRequired}
                          onBlur={trackUserDetails}
                        />
                      </div>
                      <div className="col-span-full md:col-span-1">
                        <TextField
                          name="lastName"
                          label={QUALIFY_MODAL_STRINGS['form.lastName']}
                          validate={isRequired}
                          onBlur={trackUserDetails}
                        />
                      </div>
                      <div className="col-span-full md:col-span-2">
                        <TextField
                          name="email"
                          label={QUALIFY_MODAL_STRINGS['form.email']}
                          validate={isEmail}
                          onBlur={trackUserDetails}
                        />
                      </div>
                      <div className="col-span-full md:col-span-2">
                        <TextField
                          name="phone"
                          label={QUALIFY_MODAL_STRINGS['form.phone']}
                          validate={composeValidators(isRequired, isPhone)}
                          mask={phoneNumberMask}
                          unmask={phoneNumberUnmask}
                          onBlur={trackUserDetails}
                        />
                      </div>
                    </div>
                    <Form.SubmitButton variant="solid" size="fluid">
                      {QUALIFY_MODAL_STRINGS['form.submit']}
                    </Form.SubmitButton>
                  </div>
                );
              }}
            </Form>
            <div className="my-lg px-lg w-full">
              <Divider>
                <div className="mx-xs">
                  <Text fontWeight="semibold" className="text-gray">
                    {QUALIFY_MODAL_STRINGS.divider}
                  </Text>
                </div>
              </Divider>
            </div>
            <div className="mb-sm w-full">
              <FacebookLogin
                appId={facebookAppId}
                autoLoad={false}
                isMobile={false}
                fields="name,email,picture"
                callback={handleFacebookSubmit}
                variant="secondary"
              />
            </div>
            <GoogleLogin
              clientId={googleOauthClientId}
              onSuccess={handleGoogleSuccess}
              onFailure={handleGoogleFailure}
            />
            <div className="mb-lg mt-sm w-full">
              <AppleLogin
                redirectURI={redirectURI}
                callback={handleAppleCallback}
              />
            </div>
            <div className="mb-lg">
              {QUALIFY_MODAL_STRINGS['sign_up.already_a_member']}{' '}
              <Button variant="text" onClick={handleSignIn}>
                {QUALIFY_MODAL_STRINGS['sign_up.sign_in']}
              </Button>
            </div>
            <div className="mb-lg md:mb-xl">
              <AgreeToTermsFooter />
            </div>
            <div className="contents md:hidden">
              <Link
                href={`tel:+1${phoneNumber}`}
                variant="text"
              >{`${QUALIFY_MODAL_STRINGS['phone.text']} ${phoneNumber}`}</Link>
            </div>
          </div>
        </div>
        <div className="hidden md:contents">
          <div className="border-dark-gray bg-light-gray py-md flex w-full justify-center rounded-b border-0 border-t border-solid">
            <Link href={`tel:+1${phoneNumber}`} variant="text">
              {`${QUALIFY_MODAL_STRINGS['phone.text']} ${phoneNumber}`}
            </Link>
          </div>
        </div>
      </Modal>
      <EnterContactModal
        isOpen={isEnterContactModalOpen}
        onDismiss={closeEnterContactModal}
        onSuccessfulLogin={onSuccessfulLogin}
        provider={loginProvider}
        shouldClearModals={shouldClearModals}
      />
    </>
  );
}
